'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Moon, Sun, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sleepCalculatorContent } from '../../data/sleep-calculator-content';

type AgeGroup = '0-3' | '4-6' | '7-9' | '10-13' | '14-17' | '18-25' | '26-64';

export default function SleepCalculator() {
    const [bedtime, setBedtime] = useState('22:00');
    const [waketime, setWaketime] = useState('06:00');
    const [sleepDuration, setSleepDuration] = useState('');
    const [sleepQuality, setSleepQuality] = useState('');
    const [age, setAge] = useState<AgeGroup>('26-64');
    const [isCalculating, setIsCalculating] = useState(false);

    const { toast } = useToast();

    const idealSleep: Record<AgeGroup, number> = {
        '0-3': 14,
        '4-6': 12,
        '7-9': 11,
        '10-13': 10,
        '14-17': 9,
        '18-25': 8,
        '26-64': 7,
    };

    const calculateSleep = async () => {
        if (!bedtime || !waketime) {
            toast({
                title: 'Missing Information',
                description: 'Please enter both bedtime and wake time.',
                variant: 'destructive',
            });
            return;
        }

        setIsCalculating(true);

        try {
            await new Promise((resolve) => setTimeout(resolve, 500)); // simulate delay

            const [bedHours, bedMinutes] = bedtime.split(':').map(Number);
            const [wakeHours, wakeMinutes] = waketime.split(':').map(Number);

            let hours = wakeHours - bedHours;
            let minutes = wakeMinutes - bedMinutes;
            if (hours < 0) hours += 24;
            const totalMinutes = hours * 60 + minutes;

            const durationStr = totalMinutes >= 60 ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : `${totalMinutes}m`;
            setSleepDuration(durationStr);

            const actualSleep = totalMinutes / 60;
            const qualityScore = Math.max(0, Math.min(10, 10 - Math.abs(actualSleep - idealSleep[age])));
            const quality = qualityScore >= 8 ? 'Excellent' : qualityScore >= 6 ? 'Good' : qualityScore >= 4 ? 'Fair' : 'Poor';

            setSleepQuality(quality);

            toast({
                title: 'Calculation Complete',
                description: `Sleep duration: ${durationStr}, Quality: ${quality}`,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to calculate sleep metrics.',
                variant: 'destructive',
            });
        } finally {
            setIsCalculating(false);
        }
    };

    const resetCalculator = () => {
        setBedtime('22:00');
        setWaketime('06:00');
        setSleepDuration('');
        setSleepQuality('');
        setAge('26-64');
    };

    return (
        <>
            <Toaster />
            <div className="min-h-screen bg-gray-50 py-12 px-6 sm:px-8">
                <div className="max-w-6xl mx-auto">
                    <ToolPageIntro title="Sleep Calculator - Free Online Sleep Cycle Calculator">
                Calculate optimal sleep times and understand your sleep patterns with our free sleep calculator. Improve your sleep quality and wake up refreshed.
            </ToolPageIntro>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Input Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Sleep Schedule</CardTitle>
                                <CardDescription>Enter your bedtime and wake time to calculate sleep metrics</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="bedtime">Bedtime</Label>
                                    <Input type="time" id="bedtime" value={bedtime} onChange={(e) => setBedtime(e.target.value)} className="w-full" />
                                </div>

                                <div>
                                    <Label htmlFor="waketime">Wake Time</Label>
                                    <Input type="time" id="waketime" value={waketime} onChange={(e) => setWaketime(e.target.value)} className="w-full" />
                                </div>

                                <div>
                                    <Label htmlFor="age">Age Group</Label>
                                    <Select value={age} onValueChange={(v) => setAge(v as AgeGroup)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select age group" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="0-3">0-3 years</SelectItem>
                                            <SelectItem value="4-6">4-6 years</SelectItem>
                                            <SelectItem value="7-9">7-9 years</SelectItem>
                                            <SelectItem value="10-13">10-13 years</SelectItem>
                                            <SelectItem value="14-17">14-17 years</SelectItem>
                                            <SelectItem value="18-25">18-25 years</SelectItem>
                                            <SelectItem value="26-64">26-64 years</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex space-x-2">
                                    <Button onClick={calculateSleep} disabled={isCalculating} className="flex-1">
                                        {isCalculating ? (
                                            <>
                                                <Clock className="h-4 w-4 mr-2 animate-spin" />
                                                Calculating...
                                            </>
                                        ) : (
                                            <>
                                                <Moon className="h-4 w-4 mr-2" />
                                                Calculate Sleep
                                            </>
                                        )}
                                    </Button>

                                    <Button variant="outline" onClick={resetCalculator}>
                                        <Sun className="h-4 w-4 mr-2" />
                                        Reset
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Results Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Sleep Analysis</CardTitle>
                                <CardDescription>Your sleep metrics and quality assessment</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {sleepDuration && (
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                                        <Clock className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                                        <h3 className="text-lg font-semibold text-blue-900 mb-1">Sleep Duration</h3>
                                        <p className="text-2xl font-bold text-blue-600">{sleepDuration}</p>
                                    </div>
                                )}

                                {sleepQuality && (
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                                        <Moon className="h-8 w-8 mx-auto text-green-600 mb-2" />
                                        <h3 className="text-lg font-semibold text-green-900 mb-1">Sleep Quality</h3>
                                        <p className="text-2xl font-bold text-green-600">{sleepQuality}</p>
                                        <p className="text-sm text-gray-600 mt-2">Based on your age group recommendations</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Informational Content */}
                    <div className="mt-12">
                        <InformationalContent
                            title={sleepCalculatorContent.title}
                            description={sleepCalculatorContent.description}
                            content={sleepCalculatorContent.content}
                            faqs={sleepCalculatorContent.faqs}
                            relatedTools={sleepCalculatorContent.relatedTools}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}